'use client'

/**
 * Documentation section for /products/3m-2080: the four official 3M PDFs
 * Peter supplied (scripts/supplier-assets/intake-2080-docs/), converted to
 * HTML rather than embedded as PDFs or offered as download-only links.
 * Follows the pattern established in avery-sw900.tsx / avery-prismapro.tsx.
 *
 * - Colour swatch poster: rendered to two webp sizes by
 *   scripts/supplier-assets/wire-product-docs.ts and uploaded to S3 (NOT
 *   re-run for this pass; the constants below are wired to the existing
 *   upload). Shown as a click-to-expand image. The poster covers ~85
 *   colours (High Gloss/Gloss/Satin/Matte/Textures); it is the colour
 *   reference and is deliberately NOT also transcribed as an HTML table.
 * - Product Bulletin 2080 (September 2024, supersedes July 2023): transcribed
 *   BY HAND from `pdftotext -layout 2080-product-bulletin.pdf`, in the PDF's
 *   own section order.
 * - Tips & Tricks (Commercial Solutions Division / TEBG, 2019 copyright
 *   footer): transcribed from `pdftotext -layout 2080-tips-and-tricks.pdf`.
 * - FAQ 2080 Installation, Revision F, July 2023: transcribed from
 *   `pdftotext -layout 2080-faq.pdf`, each question as its own accordion row.
 * - Warranty Program section: built from `2080-warranty-brochure.pdf`
 *   ("3M Graphics Warranties"), which is GENERIC across 3M's whole Graphics
 *   catalog -- verified by full-text grep, "2080" appears nowhere in that
 *   PDF, and its coverage matrices are organized by warranty TIER
 *   (Basic/Performance/MCS), not by film series, with no product-specific
 *   durability years. It is used here ONLY for the three-tier program
 *   explainer, kept short per that constraint. The one 2080-specific
 *   warranty figure ("up to eight years of protection on vertical
 *   applications" on the 3M MCS Warranty, "varies by zone") comes from
 *   2080-product-bulletin.txt, not the brochure, and closes this section.
 *   See scripts/supplier-assets/intake-2080-docs/sources.json for full
 *   provenance and the same warning.
 *
 * Every figure, unit, and claim is copied exactly from the source PDFs. The
 * only edits made to the extracted text are:
 * (a) three actual em-dashes (U+2014, verified against the source text)
 *     in the FAQ PDF, all appositive/parenthetical uses, replaced per house
 *     style with parentheses or commas:
 *     - "Any defect in the paint-including scratches, dents, bubbling,
 *       pitting, fisheyes, and orange peel-may be visible..." ->
 *       "Any defect in the paint (including scratches, dents, bubbling,
 *       pitting, fisheyes, and orange peel) may be visible..."
 *     - "...as that of IJ180mC-that is, slidable and repositionable
 *       adhesive with virtually invisible air release channels-but be
 *       aware..." -> "...as that of IJ180mC (that is, slidable and
 *       repositionable adhesive with virtually invisible air release
 *       channels), but be aware..."
 *     - "Apply as little heat-using only a heat gun-as possible..." ->
 *       "Apply as little heat, using only a heat gun, as possible..."
 * (b) the PDF text-layer degree-symbol artifact "○F"/"○C" (a circle glyph
 *     substituting for the real ° character, confirmed against the two
 *     spots it appears in the Product Bulletin: the pressure-activated
 *     adhesive note and the storage-conditions temperature range) is
 *     normalized to "°F"/"°C", matching how "°F"/"°C" already print
 *     correctly everywhere else in the same PDF.
 * (c) two apparent PDF-source spelling typos ("oreintation" in the Layout
 *     Considerations section, "preimum"/"apperance" in the FAQ's "What's
 *     new" answer) are left exactly as printed rather than silently
 *     corrected, since these are authored text, not extraction artifacts.
 * No figure, unit, or word was otherwise added, removed, or rounded.
 *
 * Duplicate omissions (per the "avoid duplicate rows" rule, same restraint
 * avery-sw900.tsx/avery-prismapro.tsx used): the protective-film-layer
 * handling instructions appear near-verbatim in the Product Bulletin, Tips &
 * Tricks, AND the FAQ; the fullest, most complete version (Tips & Tricks) is
 * kept as the canonical source, and the FAQ's "What's new" answer has that
 * duplicated block trimmed, keeping only its non-duplicate high-gloss-colour
 * facts. The FAQ's warranty-figure answer ("What is the warranty on the new
 * ... high gloss colors?") is a full duplicate of the Product Bulletin's
 * warranty figure and is skipped entirely, since that figure closes the
 * Warranty Program section instead. The FAQ's safety Q&A ("Are there any
 * safety concerns...") is a full duplicate of the Bulletin's static-
 * electricity WARNING callout and is skipped. The closing "Technical
 * Information" / "Warranty, Limited Remedy, and Disclaimer" / "Limitation of
 * Liability" legal boilerplate is identical, word for word, in both the
 * Product Bulletin and the FAQ; it is kept once, in the Product Bulletin
 * group, and skipped in the FAQ.
 */
import { Disclosure, DisclosureGroup } from '@heroui/react'
import type { ReactNode } from 'react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'

const POSTER_DISPLAY_URL = 'https://media.choice.graphics/product-docs/3m-2080/color-swatch-poster-display.webp'
const POSTER_FULL_URL = 'https://media.choice.graphics/product-docs/3m-2080/color-swatch-poster-full.webp'
const POSTER_ALT = '3M Wrap Film Series 2080 colour swatch poster'

// ---------------------------------------------------------------------
// Presentational helpers (same shape as avery-sw900.tsx / avery-prismapro.tsx)
// ---------------------------------------------------------------------

function KeyValueTable({ rows }: { rows: Array<{ label: string; value: ReactNode }> }) {
  return (
    <div className='rounded-lg border border-divider overflow-hidden'>
      {rows.map((row, i) => (
        <div
          key={row.label}
          className={`flex flex-col gap-1 px-4 py-2.5 text-sm sm:flex-row sm:gap-6 ${i % 2 === 0 ? 'bg-overlay' : ''}`}
        >
          <span className='text-muted font-medium shrink-0 sm:w-2/5'>{row.label}</span>
          <span className='text-foreground'>{row.value}</span>
        </div>
      ))}
    </div>
  )
}

function BulletList({ items }: { items: ReactNode[] }) {
  return (
    <ul className='list-disc space-y-1.5 pl-5 text-foreground/80'>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  )
}

interface DocSectionProps {
  id: string
  title: string
  children: ReactNode
}

function DocSection({ id, title, children }: DocSectionProps) {
  return (
    <Disclosure id={id} aria-label={title} className='overflow-hidden rounded-lg border border-divider bg-surface'>
      <Disclosure.Heading>
        {/* transform-none: HeroUI scales a pressed button to 0.97, which on a
            full-width trigger inside an overflow-hidden card leaves a sliver of
            card showing around the pressed fill. cw-ui-heading: the trigger
            sits in the Disclosure's h3, which the theme sets in Anton; the
            title stays in the body face (spec 3.2 component-heading opt-out). */}
        <Button
          slot='trigger'
          variant='ghost'
          className='h-auto w-full justify-between rounded-none px-4 py-3.5 text-left transform-none'
        >
          <span className='cw-ui-heading text-base font-semibold text-foreground'>{title}</span>
          <Disclosure.Indicator className='text-muted' />
        </Button>
      </Disclosure.Heading>
      <Disclosure.Content>
        <Disclosure.Body className='space-y-3 border-t border-divider px-4 py-4 text-sm text-foreground/80'>
          {children}
        </Disclosure.Body>
      </Disclosure.Content>
    </Disclosure>
  )
}

// ---------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------

export function M3Wrap2080Docs() {
  const [isPosterOpen, setIsPosterOpen] = useState(false)

  return (
    <div className='space-y-8'>
      {/* Poster: visible by default, not collapsed */}
      <div>
        <h3 className='text-base font-semibold text-foreground mb-3'>Colour Swatch Poster</h3>
        <button
          type='button'
          onClick={() => setIsPosterOpen(true)}
          className='group relative block w-full max-w-sm cursor-zoom-in overflow-hidden rounded-lg border border-divider'
          aria-haspopup='dialog'
          aria-label='Expand the 3M Wrap Film Series 2080 colour swatch poster'
        >
          <img
            src={POSTER_DISPLAY_URL}
            alt={POSTER_ALT}
            loading='lazy'
            decoding='async'
            className='w-full h-auto object-cover transition-transform group-hover:scale-[1.02]'
          />
          <span className='absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-xs font-medium text-white'>
            <Icon icon='fullscreen' className='w-4 h-4' />
            Expand
          </span>
        </button>
        <p className='mt-2 text-xs text-muted'>
          Full colour reference: roughly 85 colours across High Gloss, Gloss, Satin, Matte, and Textures groups.
          Expand the poster to browse them; it is the colour reference itself, not summarized as a list below.
        </p>
      </div>

      {/* Product Bulletin */}
      <div>
        <h3 className='text-base font-semibold text-foreground mb-3'>Product Bulletin</h3>
        <p className='text-xs text-muted mb-3'>Product Bulletin 2080, September 2024 (supersedes July 2023)</p>
        <DisclosureGroup allowsMultipleExpanded className='flex flex-col gap-3'>
          <DocSection id='pb-description-features' title='Product Description &amp; Features'>
            <p>
              3M™ Wrap Film Series 2080 (&ldquo;2080&rdquo;) is a multi-layer cast adhesive backed film for full
              color vehicle wraps, accents, or partial decoration wraps.
            </p>
            <BulletList
              items={[
                '2080 is a more conformable, easier to install film compared to older versions of 3M™ Wrap Film, requiring less heat during installation',
                '2080 High Gloss colors offer a 250% better reflected image quality over a typical premium cast vinyl wrap film, providing a mirror-like appearance',
                <>
                  3M™ Controltac™ and 3M™ Comply™ technology
                  <ul className='list-disc space-y-1 pl-5 mt-1.5'>
                    <li>Controltac™ minimizes the adhesive&rsquo;s initial contact area for easy sliding, tacking, snap-up, and repositioning</li>
                    <li>Comply™ allows air to exit through air release channels to the edge of the film for fast, easy, bubble-free installations</li>
                  </ul>
                </>,
                <>
                  Gloss and high gloss color films have a clear protective film layer:
                  <ul className='list-disc space-y-1 pl-5 mt-1.5'>
                    <li>Reducing surface impressions and increasing scratch resistance during installation</li>
                    <li>Enabling installers to wrap complex, curved surfaces while leaving the clear protective film layer on</li>
                  </ul>
                </>,
                <>
                  2080 has directional arrows on the liner of each roll to help install the gloss, textured,
                  metallic, pearl, and color flip films (see FAQ 2080 Installation)
                  <ul className='list-disc space-y-1 pl-5 mt-1.5'>
                    <li>
                      High gloss 2080 colors currently do NOT have directional arrows on the liner. Some high gloss
                      colors do exhibit directionality characteristics, making it important to install high gloss
                      films in a consistent orientation across the entire installation. 3M recommends checking the
                      film&rsquo;s orientation prior to installation to ensure its color and directionality will
                      match across the entire installation.
                    </li>
                  </ul>
                </>,
                'Available in 60 in. (1.52 m) rolls to allow for almost any section of the vehicle to be wrapped without seams',
                'Removable with heat and/or chemicals from most surfaces within the warranty period',
                'Comprehensive 3M™ MCS™ Warranty - up to eight years of protection on vertical applications* (See Warranty Information.)',
                'Approved for use with 3M™ Knifeless™ Tapes'
              ]}
            />
            <p className='text-xs text-muted'>* Warranty varies by zone</p>
            <p className='font-semibold text-foreground'>WARNING</p>
            <p>
              A static electricity discharge can occur when removing the liner, creating sparks and igniting
              flammable vapors. Only use 3M™ Wrap Film Series 2080 in safe environments.
            </p>
            <p className='text-xs text-muted'>
              NOTE: These products are intended for resale for professional use only. All installations should be
              conducted by a professional installer.
            </p>
            <p className='text-xs text-muted'>
              NOTE: The protective film layer on the gloss and high gloss color films can be left on for the entire
              installation and must be removed before post-heating.
            </p>
            <p className='text-xs text-muted'>
              NOTE: High gloss color films require additional considerations prior to installation. See FAQ 2080
              Installation for more details.
            </p>
          </DocSection>

          <DocSection id='pb-recommended-uses' title='Recommended Types of Graphics and End Uses'>
            <p>2080 was specifically designed for use on:</p>
            <BulletList items={['Standard vehicle graphics, including solid color, detailing, and decoration']} />
            <p className='text-xs text-muted'>
              NOTE: The intended vehicle substrate is standard OEM vehicle paint. 2080 is NOT intended for stainless
              steel or powder coated metals.
            </p>
            <p>2080 can also be used on:</p>
            <BulletList
              items={[
                'Commercial vehicle and fleet graphics, emblems, or striping',
                'Personal watercraft graphics as defined in 3M Instruction Bulletin Application: 5.42 (above the static water line only)'
              ]}
            />
          </DocSection>

          <DocSection id='pb-coverage-options' title='Coverage Options'>
            <KeyValueTable
              rows={[
                { label: 'Accents', value: 'Gas cap, rocker panels, racing stripes, chrome delete, etc.' },
                { label: 'Partial Coverage', value: 'Hood or roof examples' },
                { label: 'Full Vehicle Coverage', value: 'Complete color change' }
              ]}
            />
          </DocSection>

          <DocSection id='pb-product-line' title='Product Line &amp; Compatible Products'>
            <KeyValueTable
              rows={[
                { label: 'Product Name', value: '3M™ Wrap Film Series 2080-X' },
                { label: 'Description', value: 'X = Color code, opaque. Various colors and surfaces.' },
                { label: 'Features', value: 'Controltac™ and Comply™' }
              ]}
            />
            <p>
              Visit 3Mgraphics.com/warranties for a complete list of compatible products approved by 3M for use with
              the base film covered in this bulletin and used for the creation of graphics that may be eligible for
              the 3M™ MCS™ Warranty or 3M Performance Guarantee.
            </p>
            <p>
              Coatings or other aftermarket additions (such as, but not limited, to ceramic coatings) are excluded
              from the 3M™ MCS™ Warranty and should only be applied and/or used on a user test-and-approve basis.
              The end user assumes complete responsibility if using a coating or unsubstantiated cleaning product on
              their 2080 car wrap. 3M does NOT warrant 2080 films with coatings or other aftermarket additions.
            </p>
            <p className='font-medium text-foreground'>Graphic Protection</p>
            <p>Adding an overlaminate will make 2080 stiffer and more difficult to install.</p>
            <p className='text-xs text-muted'>
              NOTE: Printing on 2080 films is to be done strictly on a user test-and-approve basis. 3M does NOT
              warranty any printed application for 2080 film.
            </p>
            <BulletList items={['3M™ Wrap Overlaminate Series 8900']} />
          </DocSection>

          <DocSection id='pb-physical-characteristics' title='Physical Characteristics'>
            <p className='text-xs text-muted'>
              Numerical values in this table are the result of illustrative lab test measurements performed on
              unprocessed product. Results may vary, especially after the product is processed and/or installed.
              For more defined product characteristics, reach out to a local 3M sales representative.
            </p>
            <KeyValueTable
              rows={[
                { label: 'Material', value: 'Cast vinyl' },
                { label: 'Color', value: 'See the 3M™ Wrap Film Series 2080 Brochure for a full list of colors.' },
                {
                  label: 'Thickness',
                  value: (
                    <>
                      These values apply to 2080-M12. Designs vary slightly in thickness.
                      <br />
                      Without adhesive: 3.5 mil (0.09 mm)
                      <br />
                      With adhesive: 4.5 mil (0.11 mm)
                    </>
                  )
                },
                { label: 'Adhesive', value: 'Pressure-activated (slide, tack, snap-up, reposition) with air release channels' },
                { label: 'Adhesive Color', value: 'Gray* or clear, depending on film color' },
                { label: 'Typical Adhesion 24 Hours After Application', value: 'Connect with local sales representative for more detail.' },
                {
                  label: 'Liner',
                  value: (
                    <>
                      Gloss, Satin, Matte, and Textured color films: Polyethylene-coated paper
                      <br />
                      High Gloss color films: Polyethylene-coated PET
                    </>
                  )
                },
                { label: 'Flammability', value: 'Click to view the ASTM E84 report for 2080, or go to the online product catalog at 3Mgraphics.com or call 1-800-328-3908.' }
              ]}
            />
            <p className='text-xs text-muted'>* NOTE: Gray adhesive color may be darker or lighter depending on the film color.</p>
          </DocSection>

          <DocSection id='pb-application-characteristics' title='Application Characteristics'>
            <KeyValueTable
              rows={[
                { label: 'Substrate Type', value: 'OEM vehicle paint*' },
                { label: 'Application Temperature', value: '65°F to 75°F (18°C to 24°C)' },
                { label: 'Application Method', value: 'Dry only' }
              ]}
            />
            <p className='text-xs text-muted'>* Check adhesion to powder-coated or water-based paints.</p>
            <p className='font-medium text-foreground'>Factors Affecting Graphic Performance Life</p>
            <p>The actual performance life of a graphic is affected by:</p>
            <BulletList
              items={[
                'The combination of graphic materials used',
                'Selection, condition, and preparation of the substrate',
                'Surface texture',
                'Application methods',
                'Angle and direction of sun exposure',
                'Environmental conditions',
                'Cleaning and maintenance methods'
              ]}
            />
            <p className='font-medium text-foreground'>2080 Installation Over Vehicle Sensors, Radar, and Autonomous Equipment</p>
            <p>
              The 3M™ MCS™ Warranty is for aesthetics and film integrity when installed and stored appropriately
              per 3M&rsquo;s guidance. It is the vehicle owner and/or vehicle manufacturers responsibility to
              validate the compatibility of 2080 with any and all sensors or like equipment prior to installation.
              In order to ensure such equipment functions properly, 3M recommends users NEVER apply film over such
              equipment. Always test and confirm an installation does NOT create signal disruptions or loss of
              functionality prior to full adoption.
            </p>
          </DocSection>

          <DocSection id='pb-installation' title='Graphics Installation'>
            <p className='font-medium text-foreground'>Layout Considerations for Graphic Panels</p>
            <p>Take into account these considerations when planning your graphic layout:</p>
            <BulletList
              items={[
                <>
                  Textured, metallic, pearl, and color flip films may have a directional characteristic to their
                  appearance. For the best overall appearance, install the film in a consistent orientation through
                  the entire installation. 3M recommends checking the film&rsquo;s orientation and color prior to
                  installation. Do NOT alternate the film&rsquo;s oreintation from panel to panel.
                </>,
                'Film colors can vary from lot-to-lot. Use the same lot number of film for an entire vehicle to achieve a consistent appearance.'
              ]}
            />
            <p className='text-xs text-muted'>See FAQ 2080 Installation for more installation details.</p>
            <p className='font-medium text-foreground'>Handling Films</p>
            <BulletList
              items={[
                'Use a felt squeegee to install the film to minimize scratching.',
                'Use a heat gun instead of a torch when heat is needed. A torch may alter the appearance of the finish.'
              ]}
            />
            <p className='font-medium text-foreground'>3M™ Primer 94</p>
            <p>
              3M recommends using 3M™ Primer 94 to maximize a graphic&rsquo;s bond to the substrate in any areas
              that will be stretched, particularly on the edges of the film.
            </p>
            <p className='text-xs text-muted'>Consult air quality regulations before using 3M™ Primer 94.</p>
            <p className='font-medium text-foreground'>Cutting</p>
            <p>3M recommends using 3M™ Knifeless™ Tape to perform all film cutting when installing 2080 on vehicles.</p>
            <BulletList
              items={[
                'See 3M Instruction Bulletin 4.1 for sheeting, scoring, and film cutting details.',
                'See 3M Product &amp; Instruction Bulletin 4.19 Application: 3M™ Knifeless™ Tape for details on using 3M™ Knifeless™ Tape to cut film on vehicles.'
              ]}
            />
            <p className='text-xs text-muted'>
              NOTE: The thickness and texture of these films makes them more difficult to cut and weed cleanly. Use
              clean, sharp, properly aligned blades. The user must assume responsibility for the outcome, so always
              test and approve before cutting a large job.
            </p>
            <p className='font-medium text-foreground'>Application Tapes</p>
            <p>
              There are two types of application tapes. See 3M Instruction Bulletin AT-1 to determine what
              application tape is recommended for your 3M Graphics Products. Finished graphics eligible for any 3M
              warranty must be constructed using approved 3M Graphics Products. See the 3M Graphics Warranty
              Bulletin for more details.
            </p>
            <p className='font-medium text-foreground'>Premasking Tape</p>
            <p>
              Premasking tape increases stiffness during application while preventing stretching and damage. Use
              when little or no liner is exposed. See 3M Product Bulletin 4.3 for complete details.
            </p>
            <p className='font-medium text-foreground'>Prespacing Tape</p>
            <p>
              Prespacing tape holds cut and weeded letters or graphics in place during application and after
              removal of the film liner, while preventing stretching and damage. Use when large amounts of liner
              are exposed. See 3M Instruction Bulletin 4.3 for complete details.
            </p>
            <p className='text-xs text-muted'>
              NOTE: The texture on certain films in 3M™ Wrap Film Series 2080 makes it difficult for any
              application tape to adhere to the film. If used, apply the graphic as soon after cutting and applying
              an application tape as possible. Alway test and approve before committing to a job. See 3M Instruction
              Bulletin 4.3 for complete details.
            </p>
            <p className='font-medium text-foreground'>3M Related Literature</p>
            <p className='text-xs text-muted'>NOTE: Never use a wet application method to apply this film.</p>
            <p>
              In addition to other 3M bulletins specified in this document, the following bulletins provide details
              that you may need to successfully apply a graphic.
            </p>
            <BulletList
              items={[
                '3M Product & Instruction Bulletin 4.19 Application: 3M™ Knifeless Tape',
                <>
                  3M Instruction Bulletin 5.36 Application Techniques for Automobiles, Vans, and Buses
                  <ul className='list-disc space-y-1 pl-5 mt-1.5'>
                    <li>Complete the 3M Pre-Installation Inspection Record found in 5.36 prior to manufacturing or applying a graphic to an automobile, van, or bus.</li>
                  </ul>
                </>,
                '3M Instruction Bulletin 5.4 Application: Fleet Trucks',
                <>
                  3M Instruction Bulletin 5.42 Application: Special Considerations for Watercraft
                  <ul className='list-disc space-y-1 pl-5 mt-1.5'>
                    <li>Complete the 3M Pre-Installation Inspection Record found in 5.42 prior to manufacturing or applying a graphic to a watercraft.</li>
                  </ul>
                </>,
                '3M Instruction Bulletin 5.45 Application and Maintenance of Graphics for Rail Applications',
                '3M Instruction Bulletin 5.46 Application on Substrates with Recesses',
                '3M Instruction Bulletin 5.5 Application: General Procedures for Interior and Exterior Dry Applications.',
                '3M Wrap Film Product Care Guide',
                '3M Vehicle Cleaning SOP'
              ]}
            />
          </DocSection>

          <DocSection id='pb-adhesive-air-release' title='Pressure-Activated Adhesive &amp; Air Release Channels'>
            <p className='font-medium text-foreground'>Pressure-activated Adhesive</p>
            <p>The pressure activated adhesive on this film offers:</p>
            <BulletList
              items={[
                'smooth sliding into position on a substrate;',
                'fast finger tacking to check positioning; and',
                'easy snap-up and repositioning when you need it.'
              ]}
            />
            <p>The snap-up and reposition feature is lost:</p>
            <BulletList
              items={[
                'when firm pressure with a squeegee or other application tool is applied;',
                'at application temperatures above 100°F (38°C) even if only light finger pressure was used for tacking;',
                'if any part of the film is removed from the original liner and reapplied to the same or another liner; or',
                'if solvent from inkjet ink has not completely dried or cured, affecting both slideability and snap-up.'
              ]}
            />
            <p className='font-medium text-foreground'>Working with Non-Visible Air Release Channels</p>
            <p>
              Air release channels are a characteristic of films with Comply™ adhesive, allowing trapped air to
              exit through the edges of the graphic.
            </p>
            <BulletList
              items={[
                'The channels will be damaged if installers remove and attempt to change liners or reapply the same liner.',
                'For the best results, always work from the center of a graphic out to the edges to allow trapped air to escape. Use an air release tool to help remove any trapped air bubbles. See 3M Instruction Bulletin 5.4 for details.'
              ]}
            />
          </DocSection>

          <DocSection id='pb-maintenance-removal' title='Maintenance, Cleaning &amp; Removal'>
            <p className='font-medium text-foreground'>Maintenance and Cleaning</p>
            <p>
              Use care to avoid scratching the film. Never scrub the film. Scratching and abrasion marks may be
              visible and may not be able to be worked out of the film&rsquo;s unique finish. Avoid using harsh
              chemicals and brushes when cleaning the vehicle, and avoid parking near shrubs, trees, and any other
              items that could scratch the vehicle. 3M recommends storing wrapped vehicles in a temperature
              controlled garage. See 3M FAQ Vehicle Care for details.
            </p>
            <p>
              Use a cleaner designed for high-quality painted surfaces. 3M recommends users test the cleaner on a
              small area prior to full adoption. The cleaner must be wet, non-abrasive, without solvents, and have
              a pH value between 3 and 11 (neither strongly acidic nor strongly alkaline). See 3M Instruction
              Bulletin 6.5 for details.
            </p>
            <p className='text-xs text-muted'>
              NOTE: Refer to the FAQ 2080 Maintenance for a list of approved waxes and polishes to use with 2080
              films.
            </p>
            <p className='font-medium text-foreground'>Removal</p>
            <p>
              Removal requires heat and/or chemicals. 3M makes no claims as to the speed of removal. This product
              may not remove similarly to other products in the same category. See 3M Instruction Bulletin 6.5 for
              details.
            </p>
          </DocSection>

          <DocSection id='pb-shelf-life-storage' title='Shelf Life and Storage'>
            <p className='font-medium text-foreground'>Shelf Life</p>
            <p>The shelf life is never more than three years from the date of manufacture on the original box.</p>
            <p>
              Processing the film changes its shelf life to one year from the processing date, but no later than
              three years from the manufacturing date.
            </p>
            <p className='font-medium text-foreground'>Storage Conditions</p>
            <BulletList
              items={[
                '40°F to 100°F (4°C to 38°C)',
                'Out of sunlight',
                'Clean, dry area',
                'Original container',
                'Bring the film to room temperature before use'
              ]}
            />
          </DocSection>

          <DocSection id='pb-health-safety' title='Health and Safety'>
            <p className='font-medium text-foreground'>Chemicals</p>
            <p>
              When handling any chemical products, read the manufacturers&rsquo; container labels and the Safety
              Data Sheets (SDS) for important health, safety, and environmental information.
            </p>
            <BulletList
              items={[
                'Follow this link to obtain SDS sheets for 3M products.',
                'Follow this link to obtain information about substances of very high concern (SVHC) for EU products.'
              ]}
            />
            <p className='font-medium text-foreground'>Tools and Equipment Usage</p>
            <p>When using any equipment, always follow the manufacturer&rsquo;s instructions for safe operation.</p>
          </DocSection>

          <DocSection id='pb-technical-disclaimer' title='Technical Information &amp; Disclaimer'>
            <p>
              Technical information, guidance, and other statements provided by 3M are based upon records, tests,
              or experience that 3M believes to be reliable, but the accuracy, completeness, and representative
              nature of such information is not guaranteed. Such information is intended for people with knowledge
              and technical skills sufficient to assess and apply their own informed judgment to the information.
              No license to any intellectual property rights is granted or implied with respect to this technical
              information.
            </p>
            <p className='font-medium text-foreground'>Product Selection and Use</p>
            <p>
              Many factors beyond 3M&rsquo;s control and uniquely within user&rsquo;s knowledge and control can
              affect the use and performance of a 3M product in a particular application. Customer is solely
              responsible for evaluating the product and determining whether it is appropriate and suitable for
              customer&rsquo;s application, including conducting a workplace hazard assessment, reviewing all
              applicable regulations and standards, and reviewing the product label and use instructions. Failure
              to properly evaluate, select, and use a 3M product in accordance with instructions or to meet all
              applicable safety regulations may result in injury, sickness, death, and/or harm to property.
            </p>
            <p className='font-medium text-foreground'>Warranty, Limited Remedy, and Disclaimer</p>
            <p className='text-xs uppercase text-muted'>
              Unless a different warranty is specifically stated on the applicable 3M product packaging or product
              literature (in which case such warranty governs), 3M warrants that each 3M product meets the
              applicable 3M product specification at the time 3M ships the product. 3M makes no other warranties or
              conditions, express or implied, including, but not limited to, any warranty or condition of
              merchantability, fitness for a particular purpose, or arising out of a course of dealing, custom, or
              usage of trade. If a 3M product does not conform to this warranty, the sole and exclusive remedy is,
              at 3M&rsquo;s option, replacement of the 3M product or refund of the purchase price.
            </p>
            <p className='font-medium text-foreground'>Limitation of Liability</p>
            <p className='text-xs uppercase text-muted'>
              Except for the limited remedy stated above, and except to the extent prohibited by law, 3M will not
              be liable for any loss or damage arising from or related to the 3M product, whether direct, indirect,
              special, incidental, or consequential (including, but not limited to, lost profits or business
              opportunity), regardless of the legal or equitable theory asserted, including, but not limited to,
              warranty, contract, negligence, or strict liability.
            </p>
          </DocSection>
        </DisclosureGroup>
      </div>

      {/* Tips & Tricks */}
      <div>
        <h3 className='text-base font-semibold text-foreground mb-3'>Tips &amp; Tricks</h3>
        <p className='text-xs text-muted mb-3'>Commercial Solutions Division / TEBG</p>
        <DisclosureGroup allowsMultipleExpanded className='flex flex-col gap-3'>
          <DocSection id='tt-protective-film' title='Protective Film Layer'>
            <p>
              As an industry first, all 3M™ Wrap Film Series 2080 gloss films have a conformable Protective Film
              Layer over the top film surface. The benefits for installers include:
            </p>
            <BulletList
              items={[
                'Helps reduce surface impressions* during installation and removes easily and cleanly',
                'Protects against impressions and scratches and will not transfer to wrap film',
                'Conforms to wrap complexly curved body panels without removing protective film layer first',
                'Enables easier installation thanks to lowered surface friction'
              ]}
            />
            <p>
              When finishing installations around corners and panel edges, installers should peel the Protective
              Film Layer back several inches before tucking and performing their normal finishing.
            </p>
            <p>
              3M™ Knifeless Tape can easily cut through Series 2080 and the Protective Film Layer, meaning
              installers do not need to remove it before doing accenting and finishing cuts.
            </p>
            <p className='text-xs text-muted'>
              *Surface impressions are commonly known as hazing, blotchy and mottled appearances on gloss film.
            </p>
            <p className='font-medium text-foreground'>
              Leave on the Protective Film Layer for the entire installation, and remove before post-heating.
            </p>
          </DocSection>

          <DocSection id='tt-installation' title='Installation Tips &amp; Tricks'>
            <p>
              3M™ Wrap Film Series 2080 is more conformable than the previous 3M™ Wrap Film Series 1080, enabling
              easier installation with less heat.
            </p>
            <BulletList
              items={[
                'For best results with 3M™ Wrap Film Series 2080, do not overstretch material when conforming around curvature.',
                <>
                  Do not use a torch during installs with 3M™ Wrap Film Series 2080. Heat guns are far less likely
                  to haze or damage the material&rsquo;s surface or gloss level.
                  <ul className='list-disc space-y-1 pl-5 mt-1.5'>
                    <li>If hazing occurs, use a heat gun to bring the film back to its original gloss level.</li>
                  </ul>
                </>,
                '3M™ Wrap Film Series 2080 can be installed utilizing the same standard installation techniques as the previous 3M™ Wrap Film Series 1080 and 1380.',
                <>
                  3M recommends using a slip solution for best results during installation. Some examples of common
                  slip solutions are:
                  <ul className='list-disc space-y-1 pl-5 mt-1.5'>
                    <li>3M™ Paint-Protection Film Installation Gel</li>
                    <li>Mixture of mild detergent and water</li>
                  </ul>
                </>,
                'Always post-heat wrap thoroughly after installation to prevent the material from lifting over time.'
              ]}
            />
          </DocSection>
        </DisclosureGroup>
      </div>

      {/* FAQ */}
      <div>
        <h3 className='text-base font-semibold text-foreground mb-3'>FAQ</h3>
        <p className='text-xs text-muted mb-3'>FAQ 2080 Installation, Revision F, July 2023</p>
        <DisclosureGroup allowsMultipleExpanded className='flex flex-col gap-3'>
          <DocSection id='faq-whats-new' title="What's new about 3M™ Wrap Film 2080?">
            <p>
              3M™ Wrap Film Series 2080 (&ldquo;2080&rdquo;) is a multi-layer cast adhesive backed film for full
              color vehicle wraps, accents, or partial decoration wraps.
            </p>
            <p>Additional new features of 2080 high gloss color films include:</p>
            <BulletList
              items={[
                'An optimized adhesive for exceptional installation experience, related to reduced initial tack and improved repositionability*',
                'Smooth high gloss finish after installation'
              ]}
            />
            <p className='text-xs text-muted'>
              *Other 2080 colors will also have this new adhesive based on the manufacturing schedule. Contact 3M
              Product Support for more details.
            </p>
          </DocSection>

          <DocSection id='faq-vehicle-condition' title="Does the vehicle's condition impact the look of 2080?">
            <p>
              All wrap films, including 2080, should be applied to a &ldquo;like new&rdquo; vehicle finish for the
              best results. 3M&rsquo;s warranty only applies to vehicles that have an excellent bond between the
              paint and the vehicle substrate. Any rust, bubbling, scratches, dents, or other damage will be
              visible through the film, and such imperfections may damage the film or cause the film to fail
              prematurely. Areas repaired with body filler must be sanded, epoxy primed, and, for best results,
              finished with an original equipment manufacturer paint or clear coat. Any recently applied finish
              must be fully cured according to the paint manufacturer&rsquo;s directions before film is applied.
            </p>
            <p>
              Any defect in the paint (including scratches, dents, bubbling, pitting, fisheyes, and orange peel)
              may be visible through any 2080 film. This is especially a concern with the high gloss 2080 films, as
              the high gloss magnifies these defects. 3M recommends that a professional paint correction is
              performed before the installation of any 2080 films when heavy orange peeling is present on a
              vehicle. Failure to do so will result in high gloss films taking on the orange peel appearance. Wrap
              shops should set proper expectations for their customers before installing any 2080 film, especially
              when using high gloss 2080 films.
            </p>
          </DocSection>

          <DocSection id='faq-cleaning' title='How do I clean the vehicle properly before applying film?'>
            <p>
              Cleaning the application surface properly is one of the most important steps to a good application.
              Read 3M Instruction Bulletin 5.36 for complete details.
            </p>
            <p>
              Working with 2080 high gloss films requires a clean installation environment. Keep the floors clean
              of dust and debris to lessen the chance of contaminants getting under the film. Lint-free clothing
              and hair covers may also be necessary. Film should be installed immediately after the installation
              surface has been cleaned.
            </p>
          </DocSection>

          <DocSection id='faq-overlaminate' title='Can I use an overlaminate?'>
            <p>
              Applying 3M™ Wrap Overlaminate Series 8900 can alter the appearance of 2080 films&rsquo; inherent
              texture. If graphic protection other than 3M™ Wrap Overlaminate Series 8900 is used, then the wrap
              shop assumes all risk for testing, approving, suitability, and warranty.
            </p>
            <p className='text-xs text-muted'>
              IMPORTANT NOTE: All 2080 gloss and high gloss color films have a protective film layer on the
              surface. This protective film layer MUST be removed before applying an overlaminate.
            </p>
          </DocSection>

          <DocSection id='faq-printing' title='Can I print on 2080 films?'>
            <p>
              3M™ Wrap Film Series 2080 is available in many colors, textures, and finishes, and is intended to be
              used without further processing. If the film is printed, with or without graphic protection, the
              wrap shop assumes all risk for testing, approving, suitability, and warranty.
            </p>
            <p className='text-xs text-muted'>
              IMPORTANT NOTE: All 2080 gloss and high gloss film colors have a protective film layer on the
              surface. This protective film layer MUST be removed before printing.
            </p>
          </DocSection>

          <DocSection id='faq-recyclable' title='Is the protective film layer on 2080 gloss and high gloss films recyclable?'>
            <p>
              The protective film layer is classified as recyclable plastic Number 4. Recycling programs vary from
              country to country and may not be available in your area. Ask your local solid waste authority about
              their plastic Number 4 recycling program.
            </p>
          </DocSection>

          <DocSection id='faq-knifeless-tape' title='Can I use 3M™ Knifeless™ Tape with 2080 films?'>
            <p>Yes, 2080 films are compatible with 3M™ Knifeless™ Tape. See Instruction Bulletin 4.19 for further information.</p>
          </DocSection>

          <DocSection id='faq-carbon-fiber-stripe' title='Is the "striped" appearance on carbon fiber films normal?'>
            <p>
              Yes. All 2080 films have some texture integral to creating their unique looks. The carbon fiber films
              have the most texture, which appears to have more depth in certain lighting conditions, giving it a
              somewhat striped look.
            </p>
          </DocSection>

          <DocSection id='faq-lot-variation' title='Do 2080 films exhibit lot to lot variations in color?'>
            <p>
              Lot-to-lot variations are not common in 2080 film colors as 3M has specifications in place to prevent
              large color variations. However film colors can vary slighly from lot to lot. To ensure the most
              consistent color, use the same film lot number for an entire installation.
            </p>
          </DocSection>

          <DocSection id='faq-directional-color' title='Do some 2080 films appear to change color if applied in different directions?'>
            <p>
              Yes. Certain 2080 film colors, such as textured, metallic, pearl, and color flip films may have a
              directional characteristic to their appearance. To achieve the best overall apperance, install the
              film in a consistent orientation for the entire installation. 3M recommends checking the film&rsquo;s
              orientation and color prior to installation. Do NOT alternate a film&rsquo;s orientation from panel
              to panel. Follow the directional arrows on the liner when installing 2080 films to achieve
              consistency and help prevent color shifts from panel to panel. See 3M.com/2080 and 3M Product
              Bulletin 2080 for details.
            </p>
            <p className='text-xs text-muted'>
              IMPORTANT NOTE: High gloss 2080 color films currently do NOT have directional arrows on their liners.
              Some high gloss colors exhibit directionality characteristics, making it important to install high
              gloss films in a consistent orientation across the entire installation. 3M recommends checking the
              film&rsquo;s orientation prior to installation to ensure its color and directionality match across
              the entire installation.
            </p>
          </DocSection>

          <DocSection id='faq-shipping-storage' title='How can I ship and store 2080 gloss and high gloss films?'>
            <p>
              All 2080 gloss and high gloss films have a protective film layer on the surface. Do NOT use masking
              tape on the protective film layer. Wrap the outer layer of 2080 gloss and high gloss films with paper
              and fix the paper in place with tape to prevent the tape from damaging the film. Clips can also be
              used to store 2080 gloss and high gloss films. See 3M Product Bulletin 2080 for further information.
            </p>
          </DocSection>

          <DocSection id='faq-installation-details' title='Where can I get complete installation details?'>
            <p>
              3M Instruction Bulletin 5.36 provides specific instructions for installing vehicle wraps. 3M also
              offers world-class training for professional growth, as well as opportunities to earn an installer
              endorsement from 3M and be listed on the 3M website.
            </p>
          </DocSection>

          <DocSection id='faq-ij180mc-comparison' title='Does 3M™ Wrap Film Series 2080 install the same way as 3M™ Print Wrap Film IJ180mC?'>
            <p>
              Installation of 2080 films is very similar to that of 3M™ Print Wrap Film IJ180mC (&ldquo;IJ180mC&rdquo;)
              films. 2080 has the same features as IJ180mC (that is, slidable and repositionable adhesive with
              virtually invisible air release channels), but be aware of these important installation practices:
            </p>
            <BulletList
              items={[
                'Align the graphic using masking tape.',
                'Apply edge protection to the squeegee, such as PTFE tape or loop tape.',
                'Use a heat gun, never a torch.'
              ]}
            />
          </DocSection>

          <DocSection id='faq-primer' title='Is film primer required on complex curves?'>
            <p>
              3M™ Primer 94 can help any film adhere to deep channels and complex contours. Refer to 3M Instruction
              Bulletin 5.36 and 3M Instruction Bulletin 5.46 for recommendations.
            </p>
          </DocSection>

          <DocSection id='faq-wet-apply' title='Can I wet-apply 2080?'>
            <p>
              No. 3M does not recommend or warrant using a wetting solution on the vehicle surface when applying
              any film with air release channels. However, the installer may use a wetting solution on top of the
              film layer to provide some slip resistance for the squeegee.
            </p>
          </DocSection>

          <DocSection id='faq-special-considerations' title='Are there any special application considerations?'>
            <p>
              Yes, particularly for the Carbon Fiber and Metal finishes and for 3M™ Wrap Overlaminate Series 8900.
              Because of the unique patterns designed into these films for their realistic looking finish, improper
              application techniques show more readily on these finishes. Proper stretching and heating of the film
              is essential to avoid creating visual imperfections, which can appear as dents, bends, stretch marks,
              or kinks in the finish.
            </p>
            <p>To ensure a successful application:</p>
            <BulletList
              items={[
                'If stretching is required, stretch large sections of the film. Stretching the film in smaller sections may cause a series of stretch marks at every location where stretching occurred.',
                'Always use even tension when stretching the film. Avoid applying too much force in one particular area, as this may distort the film’s finish.',
                'Apply as little heat, using only a heat gun, as possible to stretch the film. The temperature at the surface of the film should not exceed 150°F (65°C).',
                'Avoid applying heat directly to where the film attaches to the substrate.'
              ]}
            />
          </DocSection>
        </DisclosureGroup>
      </div>

      {/* Warranty Program */}
      <div>
        <h3 className='text-base font-semibold text-foreground mb-3'>Warranty Program</h3>
        <p className='text-xs text-muted mb-3'>
          General 3M Graphics Warranties overview (not 2080-specific; see the 2080-specific figure below)
        </p>
        <DisclosureGroup allowsMultipleExpanded className='flex flex-col gap-3'>
          <DocSection id='wp-tiers' title='3M Graphics Warranty Program'>
            <p>
              3M organizes coverage for its Graphics Products into three tiers, described here in general terms
              from the 3M Graphics Warranties brochure (a catalog-wide overview, not specific to 2080):
            </p>
            <KeyValueTable
              rows={[
                {
                  label: '3M Basic Product Warranty',
                  value: (
                    <>
                      Out-of-the-box warranty: all 3M Graphics Products will meet 3M manufacturing specifications,
                      with no physical damage and no visual defects.
                      <p className='mt-1.5'>
                        3M warrants that 3M Graphics Products will meet applicable 3M manufacturing specifications
                        and will be free of visual defects or physical damage when removed from the original,
                        unopened 3M package. The 3M Basic Product Warranty covers all 3M Graphic Products from the
                        date of manufacture through the date of installation or expiration of the specified shelf
                        life for the 3M Graphics Product, whichever date occurs first. This warranty does not apply
                        to finished graphics.
                      </p>
                    </>
                  )
                },
                {
                  label: '3M Performance Warranty',
                  value: (
                    <>
                      Covers combinations of 3M Graphics Products when used with 3M-approved OEM branded inks;
                      ink-related defects are not covered.
                      <p className='mt-1.5'>
                        In addition to the coverage provided by the 3M Basic Warranty, the 3M Performance Warranty
                        covers 3M Graphic Products used in Finished Graphics produced and installed using approved
                        combinations of 3M Graphics Products, Performance Warranty Inks, Printers, Exposures, and
                        Applications from the date of manufacture through the warranty period specified in the
                        Warranty Matrices. This warranty does not offer warranty protection against defects,
                        failures, or losses caused by, arising from, or related to these inks.
                      </p>
                    </>
                  )
                },
                {
                  label: '3M MCS Warranty',
                  value: (
                    <>
                      The most comprehensive finished graphics warranty in the industry: covers approved
                      combinations of 3M Graphics Products and MCS Warranty Inks (if applicable), including
                      finished-graphic and ink-related defects and adhesive removal and residue.
                      <p className='mt-1.5'>
                        The 3M MCS Warranty covers 3M Graphic Products and MCS Warranty Inks (if applicable) used in
                        Finished Graphics produced and installed using the approved combinations of 3M Graphics
                        Products, MCS Warranty Inks, Printers, Exposures, and Applications from the date of
                        manufacture through the warranty period specified in the Warranty Matrices. To receive this
                        warranty coverage, Graphic Manufacturers must have a valid Certificate of 3M MCS Warranty
                        for their Printer and MCS Warranty Ink.
                      </p>
                    </>
                  )
                }
              ]}
            />
            <p className='pt-2'>
              The warranty coverage for eligible graphics is based on the user both reading and following all
              applicable and current 3M Graphics product and instruction bulletins. The warranty period for
              eligible graphics is as stated in the 3M Graphics Warranties Matrices at the time that the film was
              purchased.
            </p>
            <p className='font-medium text-foreground'>
              Comprehensive 3M™ MCS™ Warranty: up to eight years of protection on vertical applications.*
            </p>
            <p className='text-xs text-muted'>* Warranty varies by zone.</p>
          </DocSection>
        </DisclosureGroup>
      </div>

      <Modal
        isOpen={isPosterOpen}
        onClose={() => setIsPosterOpen(false)}
        title='Colour Swatch Poster'
        size='full'
        backdrop='blur'
        scrollBehavior='inside'
        closeButton
      >
        <img
          src={POSTER_FULL_URL}
          alt={POSTER_ALT}
          loading='lazy'
          decoding='async'
          className='mx-auto max-h-[85vh] w-auto max-w-full object-contain'
        />
      </Modal>
    </div>
  )
}
